import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Client } from '@shared/entities/client';
import { ClientService } from '@shared/services/api/client.service';
import { MachineFiltersService } from '@shared/services/api/machine-filters.service';
import { ProfileService } from '@shared/services/api/profile.service';
import { RequestInfoService } from '@shared/services/api/request-info.service';
import { Observable, catchError, combineLatest, first, map, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TypeRequest } from '../app.constants';
import { ThemeService } from '../services/theme.service';
import { PhoneNumberRegExp } from '@shared/utils';
import { MAXLENGTH_TEXTAREA } from '@shared/models/constants';
import { Location } from '@angular/common';
import { Roles } from '@shared/models/types';

@Component({
  selector: 'app-new-request',
  templateUrl: './new-request.component.html',
  styleUrl: './new-request.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewRequestComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  public translate = inject(TranslateService);
  private _route = inject(ActivatedRoute);
  private _themeService = inject(ThemeService);
  private _formBuilder = inject(FormBuilder);
  private _machineFilterService = inject(MachineFiltersService);
  private _profileService = inject(ProfileService);
  private _requestService = inject(RequestInfoService);
  private _clientService = inject(ClientService);
  private _location = inject(Location);

  showSuccess = false;
  showError = false;
  private _id: string | null = null;
  private _number: string | null = null;
  isLight = true;
  serialNumberList$!: Observable<any>;
  companyNameList$!: Observable<Client[]>;
  reasonsList: { label: string; value: string }[] = [];
  private _hiddenInfo = '';

  @ViewChild('fileInput') fileInput: ElementRef = {} as ElementRef;
  form!: FormGroup;
  isLoading = false;

  readonly MAXLENGTH_TEXTAREA = MAXLENGTH_TEXTAREA;

  constructor() {
    this.form = this._formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [{ value: '' }, [Validators.required, Validators.email]],
      phone: ['', [Validators.pattern(PhoneNumberRegExp)]],
      locationId: [''],
      serialNumber: [''],
      description: ['', Validators.required],
      reason: ['', Validators.required],
      attachmentBody: [''],
      attachmentFileName: ['']
    });
  }
  ngOnInit() {
    this._route.paramMap.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((params) => {
      this._id = params.get('id');
      this._number = params.get('number');
      this._cdr.markForCheck();
    });

    this.companyNameList$ = this._clientService.getAllCompanies();

    this.serialNumberList$ = combineLatest([this._route.queryParams, this._profileService.role$]).pipe(
      first(),
      switchMap(([params, role]) => {
        if (role === Roles.PARTNER) {
          this.form.get('serialNumber')?.disable();
          return of([]);
        } else if (params['serialNumber']) {
          this.form.patchValue({
            serialNumber: params['serialNumber']
          });
          this.form.get('serialNumber')?.disable();
          return of([params['serialNumber']]);
        }
        return this._machineFilterService.all$.pipe(map((filters) => filters.serialNumber));
      }),
      tap(() => this._cdr.markForCheck())
    );

    this._route.queryParams
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((params) => {
          if (params['reason'] && params['reason'] !== 'undefined') {
            this.form.patchValue({
              reason: params['reason']
            });
            this.form.get('reason')?.disable();
            this.reasonsList = [params['reason']];
          }
          this.reasonsList = TypeRequest;
          if (params['description'] && params['description'] !== 'undefined') {
            this._hiddenInfo = params['description'];
          }
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    this._profileService.profile$
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((profile) => {
          this.form.patchValue({
            firstName: profile.firstName,
            lastName: profile.lastName,
            email: profile.email,
            phone: profile.mobilePhone || profile.phone
          });
          if (profile.firstName) this.form.get('firstName')?.disable();
          if (profile.lastName) this.form.get('lastName')?.disable();
          if (profile.email) this.form.get('email')?.disable();
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((x) => {
          this.isLight = x;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  validInputs: Record<string, boolean> = {};

  submitForm() {
    const filteredValues = Object.fromEntries(Object.entries(this.form.getRawValue()).filter(([, value]) => !!value));

    this.isLoading = true;
    this._cdr.markForCheck();
    this._requestService
      .save(filteredValues)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(() => {
          this.showSuccess = true;
          this.showError = false;
          this.isLoading = false;
          this._cdr.markForCheck();
        }),
        catchError(() => {
          this.showError = true;
          this.isLoading = false;
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  back() {
    this.showSuccess = false;
    this.showError = false;
    this._cdr.markForCheck();
  }

  triggerFileUpload() {
    this.fileInput.nativeElement.click();
  }

  onFileSelect(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const file: File | null = element.files ? element.files[0] : null;

    if (file) {
      this.form.patchValue({
        attachmentFileName: file.name
      });
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader.result) {
          const [, elem] = (reader.result as string).split('base64,');
          this.form.patchValue({
            attachmentBody: elem
          });
        }
      };
    }
  }

  onSelectedCompanyName(event: Event) {
    const selectedLocationId = (event.target as HTMLSelectElement).value;
    if (selectedLocationId) {
      this._machineFilterService.currentFilters = {
        locationId: selectedLocationId
      };
      this.serialNumberList$ = this._machineFilterService.all$.pipe(map((filters) => filters.serialNumber)).pipe(
        tap(() => {
          this.form.get('serialNumber')?.enable();
          this._machineFilterService.currentFilters = {};
        })
      );
    } else {
      this._machineFilterService.currentFilters = {};
      this.serialNumberList$ = of([]);
      this.form.get('serialNumber')?.reset();
      this.form.get('serialNumber')?.disable();
    }
  }

  onResetUpdateFile() {
    this.form.patchValue({
      attachmentFileName: null
    });
  }

  goBack() {
    this._location.back();
  }
}
